import { Stack, useTheme } from "@mui/material";
import {
  getDollarValue,
  formatCurrencyAmount,
  DiscountType,
  AvailablePaymentCurrency,
  DiscountScope,
} from "@simplyk/common";
import { FieldValues, Path, UseFormRegister } from "react-hook-form";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Success } from "@/icons/outlined";

type ValidDiscountCodeBlockProps<T extends FieldValues> = {
  register: UseFormRegister<T>;
  name: Path<T>;
  discountType?: DiscountType;
  discountScope?: DiscountScope;
  discountCode?: string;
  discountAmount?: number;
  currency: AvailablePaymentCurrency;
  itemType: "ticket" | "item" | "membership";
  onRemoveClick: () => void;
};

const APPLIED_TO_SPECIFIC_RATE_KEYS = {
  ticket: "validDiscountCodeAppliedToSpecificRate",
  item: "validDiscountCodeAppliedToSpecificRateItem",
  membership: "validDiscountCodeAppliedToSpecificRateMembership",
} as const;

export function ValidDiscountCodeBlock<T extends FieldValues>({
  register,
  name,
  currency,
  discountType,
  discountScope,
  discountCode,
  discountAmount,
  itemType,
  onRemoveClick,
}: ValidDiscountCodeBlockProps<T>) {
  const theme = useTheme();
  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();

  const getValidDiscountLabel = (): string => {
    if (!discountCode) {
      return t("ticketing", "discountCodeField.valid.defaultLabel");
    }

    return discountType === DiscountType.Dollars
      ? formatCurrencyAmount({
          value: getDollarValue(discountAmount ?? 0),
          locale: isoLocale,
          currency,
        })
      : `${discountAmount}%`;
  };

  return (
    <Stack
      role="status"
      direction={{ sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      gap={2}
      sx={{
        padding: 2,
        color: theme.palette.formsComponents.paymentSummary.textColor,
        backgroundColor: theme.palette.formsComponents.paymentSummary.backgroundColor,
        border: `1px solid ${theme.palette.formsComponents.paymentSummary.borderColor}`,
        borderRadius: theme.radius(1),
      }}
    >
      <input type="hidden" {...register(name)} />
      <Stack direction="row" alignItems="center" gap={2} flexGrow={{ sm: 1 }}>
        <Stack direction="column" gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <FormIcon sx={{ display: { xs: "none", sm: "block" } }}>
              <Success />
            </FormIcon>
            <FormTypography variant="body2" sx={{ flexGrow: 1 }}>
              <Trans
                space="ticketing"
                i18nKey="validDiscountCodeApplied"
                components={{ emphasis: <FormTypography variant="subtitle2" component="span" /> }}
                values={{
                  discountCode,
                  discountValue: getValidDiscountLabel(),
                }}
              />
            </FormTypography>
          </Stack>
          {discountScope === DiscountScope.PerTicketSpecificRates && (
            <FormTypography variant="body2">{t("ticketing", APPLIED_TO_SPECIFIC_RATE_KEYS[itemType])}</FormTypography>
          )}
        </Stack>
      </Stack>
      <FormButton size="small" variant="outline" onClick={onRemoveClick}>
        {t("common", "remove")}
      </FormButton>
    </Stack>
  );
}
